import { API_CONFIG, fetchApi } from "./api";

const BASE_URL = API_CONFIG.MEASUREMENTS_SERVICE;

// Types
export type MeasurementSource = "MANUAL" | "NFC" | "API" | "IMPORT";
export type ReadingStatus = "OPTIMAL" | "WARNING" | "CRITICAL";

export interface Reading {
  parameterId: string;
  value: number;
  unit?: string;
  status?: ReadingStatus;
}

export interface Measurement {
  id: string;
  poolId: number;
  technicianId?: number;
  source: MeasurementSource;
  deviceId?: string;
  readings: Reading[];
  timestamp: string;
  createdAt: string;
  notes?: string;
}

export interface MeasurementCreateRequest {
  poolId: number;
  technicianId?: number;
  source?: MeasurementSource;
  deviceId?: string;
  readings: Reading[];
  timestamp?: string;
  notes?: string;
}

// Measurements API - Note: BASE_URL ya incluye /api/v1
export const measurementApi = {
  getAll: () => fetchApi<Measurement[]>(`${BASE_URL}/measurements`),

  getById: (id: string) =>
    fetchApi<Measurement>(`${BASE_URL}/measurements/${id}`),

  getByPoolId: (poolId: number) =>
    fetchApi<Measurement[]>(`${BASE_URL}/measurements/pool/${poolId}`),

  getLatestByPoolId: async (poolId: number): Promise<Measurement> => {
    try {
      return await fetchApi<Measurement>(
        `${BASE_URL}/measurements/pool/${poolId}/latest`,
      );
    } catch (error) {
      // When database is empty, backend returns 404 - mark it for caller
      if (error instanceof Error && error.message.includes("404")) {
        const notFoundError = new Error(
          `No measurement found for pool ${poolId}`,
        );
        (notFoundError as any).status = 404;
        throw notFoundError;
      }
      throw error;
    }
  },

  getRecentByPoolId: (poolId: number) =>
    fetchApi<Measurement[]>(`${BASE_URL}/measurements/pool/${poolId}/recent`),

  getByDateRange: (poolId: number, startDate: string, endDate: string) =>
    fetchApi<Measurement[]>(
      `${BASE_URL}/measurements/pool/${poolId}/range?startDate=${startDate}&endDate=${endDate}`,
    ),

  getByTechnicianId: (technicianId: number) =>
    fetchApi<Measurement[]>(
      `${BASE_URL}/measurements/technician/${technicianId}`,
    ),

  getBySource: (source: MeasurementSource) =>
    fetchApi<Measurement[]>(`${BASE_URL}/measurements/source/${source}`),

  create: (data: MeasurementCreateRequest) =>
    fetchApi<Measurement>(`${BASE_URL}/measurements`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    fetchApi<void>(`${BASE_URL}/measurements/${id}`, { method: "DELETE" }),

  countByPoolId: (poolId: number) =>
    fetchApi<number>(`${BASE_URL}/measurements/pool/${poolId}/count`),

  countTodayByPoolId: (poolId: number) =>
    fetchApi<number>(`${BASE_URL}/measurements/pool/${poolId}/count/today`),
};
